/**
 * BarChart: one bar per category, drawn from plain data with plain props (ADR-026)
 * on Recharts. Rounded tops carry --q-radius, both axes are labeled, the legend
 * carries token swatches, the tooltip names the bar, and the click-through contract
 * is preserved through Recharts' own click events.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, CHART_MARGIN, Legend, seriesClass, seriesColor, surfaceClass } from "./shared";

export interface BarDatum {
  readonly label: string;
  readonly value: number;
}

/** The section radius token as a number for the bar tops. */
const RADIUS = 6;

interface BarShapeProps {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
}

/**
 * A bar with a rounded top only: --q-radius on the two top corners, square at the
 * baseline. Drawn as a path so the rounding never eats into the axis.
 */
function topRoundedRect(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): string {
  const r = Math.min(radius, width / 2, Math.max(height, 0));
  const end = y + height;
  return [
    `M ${String(x)} ${String(end)}`,
    `L ${String(x)} ${String(y + r)}`,
    `Q ${String(x)} ${String(y)} ${String(x + r)} ${String(y)}`,
    `L ${String(x + width - r)} ${String(y)}`,
    `Q ${String(x + width)} ${String(y)} ${String(x + width)} ${String(y + r)}`,
    `L ${String(x + width)} ${String(end)}`,
    "Z",
  ].join(" ");
}

export function BarChart({
  ariaLabel,
  data,
  unit,
  onSelectDatum,
  frameClassName,
  xAxisLabel,
  tickAngle,
  tickInterval,
}: {
  ariaLabel: string;
  data: readonly BarDatum[];
  /** Optional unit word for the value axis tick, e.g. "inspections". */
  unit?: string;
  /**
   * Optional click-through (the chart contract): one callback per bar. Only wired
   * when the bar has a natural filtered register behind it; never a dead point.
   */
  onSelectDatum?: (datum: BarDatum, index: number) => void;
  /**
   * Optional modifier or replacement class for the chart frame (e.g. "q-chart__frame--lead"
   * for a tighter 640/180 register-lead placement).
   */
  frameClassName?: string;
  /**
   * Optional label for the horizontal axis. When omitted/undefined, no category
   * label is rendered (avoiding spurious hard-coded "Category" labels).
   */
  xAxisLabel?: string;
  /**
   * Optional rotation angle in degrees for X-axis tick labels to avoid collisions.
   */
  tickAngle?: number;
  /**
   * Optional tick interval strategy or count for X-axis labels.
   */
  tickInterval?: number | "preserveStart" | "preserveEnd" | "preserveStartEnd";
}): React.JSX.Element {
  const total = data.reduce((sum, datum) => sum + datum.value, 0);
  if (data.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  const shape = (props: BarShapeProps & { readonly index?: number }): React.JSX.Element | null => {
    const { x, y, width, height, index } = props;
    if (x === undefined || y === undefined || width === undefined || height === undefined) {
      return null;
    }
    const datum = data[index ?? 0];
    const name =
      datum === undefined
        ? undefined
        : unit === undefined
          ? strings.charts.mark(datum.label, datum.value)
          : `${strings.charts.mark(datum.label, datum.value)} ${unit}`;
    const clickable =
      datum === undefined
        ? undefined
        : onSelectDatum === undefined
          ? undefined
          : () => {
              onSelectDatum(datum, index ?? 0);
            };
    // The keyboard reach of the click contract stays on the mark itself; the
    // pointer click goes through Recharts' Bar onClick below.
    return (
      <path
        d={topRoundedRect(x, y, width, height, RADIUS)}
        fill={seriesColor(index ?? 0)}
        className={
          onSelectDatum === undefined
            ? seriesClass(index ?? 0)
            : `${seriesClass(index ?? 0)} q-chart__hit`
        }
        tabIndex={onSelectDatum === undefined ? undefined : 0}
        aria-label={name}
        role="img"
        onKeyDown={
          onSelectDatum === undefined || clickable === undefined
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  clickable();
                }
              }
        }
      >
        {name === undefined ? null : <title>{name}</title>}
      </path>
    );
  };

  const frameClass = frameClassName ? `q-chart__frame ${frameClassName}` : "q-chart__frame";

  const xAxisExtra: {
    angle?: number;
    textAnchor?: "end" | "start" | "middle";
    height?: number;
    label?: { value: string; position: "insideBottom"; offset: number };
  } = {};
  if (tickAngle !== undefined && tickAngle !== 0) {
    xAxisExtra.angle = tickAngle;
    xAxisExtra.textAnchor = tickAngle < 0 ? "end" : "start";
    xAxisExtra.height = 48;
  }
  if (xAxisLabel !== undefined && xAxisLabel.length > 0) {
    xAxisExtra.label = { value: xAxisLabel, position: "insideBottom", offset: -2 };
  }

  return (
    <figure className="q-chart" data-testid="bar-chart">
      <div className={frameClass}>
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <RechartsBarChart
            data={[...data]}
            margin={CHART_MARGIN}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <CartesianGrid stroke="var(--q-rule)" vertical={false} />
            <XAxis
              dataKey="label"
              interval={tickInterval ?? 0}
              tickLine={false}
              axisLine={{ stroke: "var(--q-rule-strong)" }}
              {...xAxisExtra}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number): string => String(Math.round(value))}
              label={{
                value: unit ?? strings.charts.valueAxis,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />
            <Tooltip
              isAnimationActive={false}
              cursor={{ fill: "var(--q-sig-wash)" }}
              content={({ active: hover, payload, label }) =>
                hover && payload.length > 0 ? (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{String(label)}</p>
                    <ul className="q-chart__tooltip-list">
                      {payload.map((entry, index) => (
                        <li key={index} className="q-chart__tooltip-row">
                          <span
                            aria-hidden="true"
                            className="q-chart__tooltip-swatch"
                            style={{ background: entry.color }}
                          />
                          {entry.name}: {String(entry.value)}
                          {unit === undefined ? "" : ` ${unit}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              }
            />
            <Bar
              dataKey="value"
              shape={shape}
              isAnimationActive={false}
              onClick={(_datum, index) => {
                if (onSelectDatum === undefined) {
                  return;
                }
                const picked = data[index];
                if (picked !== undefined) {
                  onSelectDatum(picked, index);
                }
              }}
            />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      <Legend entries={data.map((datum, index) => ({ label: datum.label, index }))} />
    </figure>
  );
}
